// External dependencies
import { FC, useContext, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';
import { withWhiteLabelContext } from 'react-whitelabel';

// Global
import { theme } from './theme';
import RefreshScaffold from './Scaffold';
import 'font-awesome/css/font-awesome.css';
import 'react-toastify/dist/ReactToastify.css';

// Legacy
import Login from '../components/containers/login/login';
import JoinLiveSession from '../components/containers/join-live-session/join-live-session';
import SplitIORoute from '../components/splitio/splitioRoute';

// New UI
import SsoRedirect from './utils/ssoRedirect';
import LogOut from './pages/LogOut';
import SignIn from './pages/SignIn';
import ForgotPassword from './pages/ForgotPassword';
import ResetPasswordRedirect from './pages/ResetPasswordRedirect';
import ChurnZero from './components/ChurnZero';
import WebcoreIframePreloader from './components/WebcoreIframePreloader';
import MixPanelAggregator from './components/MixPanelAggregator';
import AuthContext from '../context/AuthContext';
import { authInterceptor } from './utils/authInterceptor';
import OrganizationDropdown from './components/OrganizationDropdown';
import { KeycloakPrivateRoute } from './components/PrivateRoute';

interface IApp {
  label: {
    appTitle: string;
    faveIcon: string;
  };
  realm: string;
}

const App: FC<IApp> = ({ label, realm }) => {
  const keycloak = useContext(AuthContext);
  const [keycloakReady, setKeycloakReady] = useState(false);
  const [orgId, setOrgId] = useState<string | undefined>();

  useEffect(() => {
    keycloak.onAuthRefreshError = () => {
      keycloak.login();
    };

    const initKeycloak = async () => {
      try {
        await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
          scope: 'openid profile organizations user-attributes',
          checkLoginIframeInterval: 5,
        });

        setKeycloakReady(true);
      } catch (error) {
        console.error('Failed to initialize adapter:', error);
      }
    };

    if (!keycloakReady && !keycloak.token) {
      initKeycloak();
    }
  }, [keycloak]);

  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    authInterceptor(keycloak, realm, e.target.value);
    setOrgId(e.target.value);
  };

  if (keycloakReady) {
    authInterceptor(keycloak, realm);

    return (
      <ThemeProvider theme={theme}>
        <Helmet>
          <title>{label.appTitle}</title>
          <link rel="shortcut icon" href={label.faveIcon} />
        </Helmet>

        <ChurnZero />
        <MixPanelAggregator />
        <WebcoreIframePreloader />

        <Switch>
          <SplitIORoute
            exact
            path="/forgot-password/"
            onComponent={ForgotPassword}
            offComponent={Login}
            featureName={'Forgot-Password'}
          />
          <SplitIORoute
            exact
            path="/reset/"
            onComponent={ResetPasswordRedirect}
            offComponent={Login}
            featureName={'Forgot-Password'}
          />
          <Route exact path="/together" component={JoinLiveSession} />
          <Route exact path="/sso/marble" component={SsoRedirect} />
          <SplitIORoute
            path="/login"
            onComponent={SignIn}
            offComponent={Login}
            featureName={'DC-42-UI-refresh-anonymous'}
          />
          <Route exact path="/log-out" component={LogOut} />

          <KeycloakPrivateRoute>
            <RefreshScaffold />
          </KeycloakPrivateRoute>
        </Switch>

        <OrganizationDropdown activeOrganization={orgId} selectHandler={handleOrgChange} />
      </ThemeProvider>
    );
  }

  return <>Loading...</>;
};

export default withWhiteLabelContext(App);
